import { BN } from '@apps-orangefi/lib'
import { Pool, PoolToken, Token } from '@apps-orangefi/lib/types'
import { TickMath } from '@uniswap/v3-sdk'
import JSBI from 'jsbi'
import {
  Address,
  keccak256,
  pad,
  concat,
  toBytes,
  encodeAbiParameters,
  parseAbiParameters,
} from 'viem'

import { getAmountsForLiquidity } from './price'
import { getTokenPair } from './token'

export type DopexStrikeEarningPerVault = {
  sqrtPriceX96?: string
  compound?: string
  donation?: string
  strike: {
    id: string
    pool: string
    tickLower: number
    tickUpper: number
    totalLiquidity: string
    totalShares: string
  }
  pool: string
  shares: string
  user: string
  handler: string
}

type Vault = {
  id: string
  totalAssets: string
  totalSupply: string
  decimals: string
  isTokenPairReversed: boolean
}

export const calculateAPR = (
  earningList: DopexStrikeEarningPerVault[],
  vault: Vault,
  pool: Pool,
  ethPriceUSD: BN
) => {
  const strikeEarningAmounts = getStrikeEarningAmounts(earningList)
  const result = strikeEarningAmounts.map(earn => {
    const {
      amount0ETH: feeAmount0ETH,
      amount1ETH: feeAmount1ETH,
      totalAmountETH: feeTotalAmountETH,
      totalAmountUSD: feeTotalAmountUSD,
    } = convertTotalAmountEthAndUsd(
      earn.fee.amount0,
      earn.fee.amount1,
      pool.token0,
      pool.token1,
      ethPriceUSD
    )
    const {
      amount0ETH: premiumAmount0ETH,
      amount1ETH: premiumAmount1ETH,
      totalAmountETH: premiumTotalAmountETH,
      totalAmountUSD: premiumTotalAmountUSD,
    } = convertTotalAmountEthAndUsd(
      earn.premium.amount0,
      earn.premium.amount1,
      pool.token0,
      pool.token1,
      ethPriceUSD
    )
    const {
      amount0: orangeAmount0,
      amount1: orangeAmount1,
      totalAmountETH: orangeTotalAmountETH,
      totalAmountUSD: orangeTotalAmountUSD,
    } = convertTotalAmountEthAndUsd(
      earn.orange.amount0,
      earn.orange.amount1,
      pool.token0,
      pool.token1,
      ethPriceUSD
    )

    const feeAPR = feeTotalAmountETH
      .multipliedBy(earn.rate)
      .dividedBy(orangeTotalAmountETH)
      .multipliedBy(365)

    const premiumAPR = premiumTotalAmountETH
      .multipliedBy(earn.rate)
      .dividedBy(orangeTotalAmountETH)
      .multipliedBy(365)

    return {
      id: earn.tokenId,
      feeAmount0ETH,
      feeAmount1ETH,
      feeTotalAmountETH,
      feeTotalAmountETHOfOrangeShare: feeTotalAmountETH.multipliedBy(earn.rate),
      premiumAmount0ETH,
      premiumAmount1ETH,
      premiumTotalAmountETH,
      premiumTotalAmountETHOfOrangeShare: premiumTotalAmountETH.multipliedBy(earn.rate),
      orangeAmount0,
      orangeAmount1,
      orangeTotalAmountETH,
      feeAPR,
      premiumAPR,
      rate: earn.rate,
    }
  })
  const [baseToken] = getTokenPair(vault, pool as Pool)
  const tvl = new BN(vault.totalAssets)
    .dividedBy(10 ** Number(baseToken.decimals))
    .times(baseToken.derivedETH)
    .times(ethPriceUSD)

  const res = result.reduce(
    (acc, sp) => {
      acc.fee = acc.fee.plus(sp.feeTotalAmountETHOfOrangeShare)
      acc.premium = acc.premium.plus(sp.premiumTotalAmountETHOfOrangeShare)
      acc.orange = acc.orange.plus(sp.orangeTotalAmountETH)
      return acc
    },
    { fee: new BN(0), premium: new BN(0), orange: new BN(0) }
  )

  return {
    vaultId: vault.id,
    fee: res.fee,
    premium: res.premium,
    orange: res.orange,
    dopexApr: !res.orange.isZero()
      ? res.fee.plus(res.premium).dividedBy(res.orange).multipliedBy(365)
      : new BN(0),
    strikePremimFee: result,
    tvl,
  }
}

export const getStrikeEarningAmounts = (earningList: DopexStrikeEarningPerVault[]) => {
  if (!earningList) return []

  return earningList.map(earning => {
    if (!earning.strike || !earning.sqrtPriceX96 || !earning.donation || !earning.compound) {
      return {
        tokenId: earning.strike.id,
        fee: { amount0: JSBI.BigInt(0), amount1: JSBI.BigInt(0) },
        premium: { amount0: JSBI.BigInt(0), amount1: JSBI.BigInt(0) },
        orange: { amount0: JSBI.BigInt(0), amount1: JSBI.BigInt(0) },
        rate: new BN(0),
      }
    }
    const sqrtPriceX96 = JSBI.BigInt(earning.sqrtPriceX96)
    const sqrtRatioAX96 = TickMath.getSqrtRatioAtTick(earning.strike.tickLower)
    const sqrtRatioBX96 = TickMath.getSqrtRatioAtTick(earning.strike.tickUpper)
    const donations = JSBI.BigInt(earning.donation)
    const compouds = JSBI.BigInt(earning.compound)
    const premiumAmounts = getAmountsForLiquidity(
      sqrtPriceX96,
      sqrtRatioAX96,
      sqrtRatioBX96,
      donations
    )
    const feeAmounts = getAmountsForLiquidity(sqrtPriceX96, sqrtRatioAX96, sqrtRatioBX96, compouds)

    const totalLiquidity = new BN(earning.strike.totalLiquidity.toString())
    const shares = new BN(earning.shares.toString() ?? 0)
    const totalShares = new BN(earning.strike.totalShares.toString())
    const orangeShareRate =
      shares.isZero() || totalShares.isZero() ? new BN(0) : shares.dividedBy(totalShares)
    const orangeLiquidity = totalLiquidity.multipliedBy(orangeShareRate).toFixed(0)

    const orangeAmounts = getAmountsForLiquidity(
      sqrtPriceX96,
      sqrtRatioAX96,
      sqrtRatioBX96,
      JSBI.BigInt(Number(orangeLiquidity))
    )

    return {
      tokenId: earning.strike.id,
      fee: feeAmounts,
      premium: premiumAmounts,
      orange: orangeAmounts,
      rate: shares.dividedBy(totalShares),
    }
  })
}

export const convertTotalAmountEthAndUsd = (
  amount0: JSBI | BN,
  amount1: JSBI | BN,
  token0: Token | PoolToken,
  token1: Token | PoolToken,
  ethPriceUSD: BN
) => {
  const _amount0 = amount0 instanceof JSBI ? new BN(amount0.toString()) : amount0
  const _amount1 = amount1 instanceof JSBI ? new BN(amount1.toString()) : amount1
  const { decimalizeAmount: decimalizeAmount0, amountETH: amount0ETH } = convertAmountToETH(
    _amount0,
    token0
  )
  const { decimalizeAmount: decimalizeAmount1, amountETH: amount1ETH } = convertAmountToETH(
    _amount1,
    token1
  )

  const totalAmountETH = amount0ETH.plus(amount1ETH)
  const totalAmountUSD = totalAmountETH.times(ethPriceUSD)

  return {
    amount0: decimalizeAmount0,
    amount1: decimalizeAmount1,
    amount0ETH,
    amount1ETH,
    totalAmountETH,
    totalAmountUSD,
  }
}

export const convertAmountToETH = (amount: BN, token: Token | PoolToken) => {
  const tokenDecimal = 10 ** Number(token.decimals)
  const decimalizeAmount = amount.div(tokenDecimal)
  const amountETH = decimalizeAmount.times(token.derivedETH)

  return { decimalizeAmount, amountETH }
}

export function genTokenId(
  handler: AddressType,
  pool: AddressType,
  hook: AddressType,
  tickLower: number,
  tickUpper: number
): string {
  // Encode each parameter
  let encodedHandler = encodeAddress(handler)
  let encodedPool = encodeAddress(pool)
  let encodedHook = encodeAddress(hook)
  let encodedTickLower = encodeInt24(tickLower)
  let encodedTickUpper = encodeInt24(tickUpper)

  // Concatenate all byte arrays
  let encodedData = concat([
    encodedHandler,
    encodedPool,
    encodedHook,
    encodedTickLower,
    encodedTickUpper,
  ])

  // Compute keccak256 hash and convert to bigint
  return BigInt(keccak256(encodedData)).toString()
}

/**
 * Encode an address to 32 bytes
 */
function encodeAddress(address: Address): Uint8Array {
  return pad(toBytes(address), { size: 32 })
}

/**
 * Encode an int24 to 32 bytes with sign extension
 */
function encodeInt24(value: number): Uint8Array {
  // ABI エンコーディングを使用して int24 を適切にエンコード
  const encoded = encodeAbiParameters(parseAbiParameters('int24'), [value])

  return pad(toBytes(encoded), { size: 32 })
}
